import axios from 'axios';
import { CheckCircle2, Loader2, MailWarning } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { AuthLayout } from '../auth/AuthLayout';

type State =
  | { kind: 'loading' }
  | { kind: 'ok' }
  | { kind: 'already' }
  | { kind: 'invalid' }
  | { kind: 'error'; message: string };

export function VerifyEmailScreen() {
  const [params] = useSearchParams();
  const token = params.get('token');
  const [state, setState] = useState<State>({ kind: 'loading' });

  useEffect(() => {
    if (!token) {
      setState({ kind: 'invalid' });
      return;
    }
    let cancelled = false;
    axios
      .post(
        '/api/auth/verify-email',
        { token },
        { withCredentials: false, headers: { Accept: 'application/json' } },
      )
      .then((res) => {
        if (cancelled) return;
        const data = res.data as { alreadyVerified?: boolean } | undefined;
        if (data?.alreadyVerified) setState({ kind: 'already' });
        else setState({ kind: 'ok' });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const status = (err as { response?: { status?: number } }).response?.status;
        if (status === 400 || status === 404 || status === 410)
          setState({ kind: 'invalid' });
        else
          setState({
            kind: 'error',
            message: "We couldn't verify your email. Please try again.",
          });
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  switch (state.kind) {
    case 'loading':
      return (
        <AuthLayout title="Verifying your email…">
          <div className="flex justify-center py-4">
            <Loader2 className="size-8 animate-spin text-muted-foreground" />
          </div>
        </AuthLayout>
      );
    case 'ok':
      return (
        <AuthLayout
          title="Email verified"
          subtitle="You're all set. Continue to your studio."
          footer={null}
        >
          <div className="flex flex-col items-center gap-4 py-2">
            <CheckCircle2 className="size-10 text-success" />
            <Button asChild size="lg" className="w-full">
              <Link to="/sign-in">Continue</Link>
            </Button>
          </div>
        </AuthLayout>
      );
    case 'already':
      return (
        <AuthLayout
          title="Already verified"
          subtitle="Your email was verified earlier — sign in to keep working."
        >
          <Button asChild size="lg" className="w-full">
            <Link to="/sign-in">Sign in</Link>
          </Button>
        </AuthLayout>
      );
    case 'invalid':
      return (
        <AuthLayout
          title="Link expired"
          subtitle="This verification link is no longer valid. Sign in and request a new one from the verify-email screen."
        >
          <div className="flex flex-col items-center gap-3 py-2">
            <MailWarning className="size-10 text-warning" />
            <Button asChild size="lg" className="w-full">
              <Link to="/sign-in">Sign in</Link>
            </Button>
          </div>
        </AuthLayout>
      );
    case 'error':
      return (
        <AuthLayout
          title="Something went wrong"
          subtitle={state.message}
        >
          <Button
            size="lg"
            className="w-full"
            onClick={() => window.location.reload()}
          >
            Try again
          </Button>
        </AuthLayout>
      );
  }
}
